import { Sound, Vtype } from '@components/AppNexus/types'
import { registerSlot as admTagManRegisterSlot } from '@components/AppNexus/utils'
import config from '@config'
import contextViewport, {
  ViewportOrientationValues,
  ViewportTypeValues,
} from '@contexts/viewport'
import useAds from '@hooks/useAds'
import { useBlickTV } from '@hooks/useBlickTV'
import useIsServerSideRendered from '@hooks/useIsServerSideRendered'
import useRiAdTagManagerInit from '@hooks/useRiAdTagManagerInit'
import { useVideo } from '@hooks/useVideo'
import { getCurrentAdViewportType, logAdMessage } from '@utils/ads'
import { captureException } from '@utils/sentry'
import { useRouter } from 'next/router'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getNextVideoInPlaylist } from '../playlist/utils'
import useBlickTVStory from '../story/useBlickTVStory'
import {
  AdTagManagerDaiResponse,
  IABCategory,
  UseCommonPropsOutputProps,
  UsePlatformSpecificPropsInput,
  UsePlatformSpecificPropsWebOutput,
  VideoPlayingStatus,
  BrightcoveVideoWebPlayer,
} from '../types/darkcove'
import { getDefaultDaiResponse } from './defaultDaiResponse'
import useCommonProps from './useCommonProps'
import usePlaylist from './usePlaylist'
import useVideoRecomendations from './useVideoRecomendations'

const {
  video: { playerId },
} = config

const { ViewportTypeContext, ViewportOrientationContext } = contextViewport

interface RegisterAdProps
  extends Pick<
      UsePlatformSpecificPropsInput,
      'isBlickTV' | 'syndication' | 'videoTaxonomy'
    >,
    Pick<UseCommonPropsOutputProps, 'vtype'> {
  adContainerId: string
  sound: boolean
  autostart: string
  handleAd: ({
    vmapUrl,
    daiVastUrl,
    message,
  }: {
    vmapUrl?: string
    daiVastUrl?: AdTagManagerDaiResponse
    message?: any
  }) => void
  areAdSlotsLoaded: () => boolean
  loadAllSlots: () => void
}

const registerAd = ({
  isBlickTV,
  vtype,
  adContainerId,
  syndication,
  videoTaxonomy,
  sound,
  autostart,
  handleAd,
  areAdSlotsLoaded,
  loadAllSlots,
}: RegisterAdProps) => {
  const BlickTVSpecificProps = {
    source: 'Blick TV Player',
    targeting: {
      vtype,
      sound: (sound ? 'on' : 'off') as Sound,
      player: 'Brightcove',
    },
    channel: 'BlickTV',
    slot: 'MIDROLL_1',
  }

  const VODSpecificProps = {
    source: 'Video Player',
    targeting: {
      vtype,
      syndication,
      sherlock: videoTaxonomy?.map(({ id }: IABCategory) => id) ?? [],
      sound: (sound ? 'on' : 'off') as Sound,
      autostart,
      player: 'Brightcove',
    },
  }

  // Register the VMAP adslot
  admTagManRegisterSlot({
    slot: 'VMAP_1',
    container: adContainerId,
    events: {
      adReady: handleAd,
      adEmpty: handleAd,
      adError: handleAd,
    },
    ...(isBlickTV ? BlickTVSpecificProps : VODSpecificProps),
  })

  if (areAdSlotsLoaded()) {
    loadAllSlots()
  }

  logAdMessage(
    `ADS: registered slot for ${getCurrentAdViewportType()} for: VMAP_1`
  )
}

export const usePlatformSpecificPropsWeb = ({
  isBlickTV,
  isInViewport,
  allowFloating,
  isAdDisabled,
  syndication,
  videoTaxonomy,
  image,
  position,
  autoplay,
  videoId,
  allowVideoCropping,
  duration,
  switchHeadline,
  title,
  isInArticle,
  isInVideoOnlyArticle,
  isMainElement,
  hasPlaylist,
  daiAssetKeyBrightcove,
}: UsePlatformSpecificPropsInput): UsePlatformSpecificPropsWebOutput => {
  const internalPlayerRef = useRef<BrightcoveVideoWebPlayer>(null)

  const { playerRef: blickTVContextPlayerRef, popOut, unmount } = useBlickTV()

  const playerRef =
    isBlickTV && allowFloating && blickTVContextPlayerRef.current
      ? blickTVContextPlayerRef.current
      : internalPlayerRef

  const { trackingCallbacks, vtype, ...restOfCommonProps } = useCommonProps({
    playerRef,
    isBlickTV,
    allowFloating,
    //! For now we only allow autoplay for video only articles so we can
    //! set autoplay to the content type video
    //! As soon as autoplay is configurable through the backend
    //! we have to change that here
    autoplay: isInVideoOnlyArticle,
    videoId,
    title,
    position,
    allowVideoCropping,
    image,
    duration,
    isAdDisabled,
    switchHeadline,
    isInVideoOnlyArticle,
    isInViewport,
  })

  const router = useRouter()
  const isSSR = useIsServerSideRendered()
  const playlistVideosData = usePlaylist()
  const tagManagerInitCode = useRiAdTagManagerInit()
  const previousOrientationRef = useRef<ViewportOrientationValues>()
  const vmapUrlRef = useRef<string | null>(null)
  const hasAdRegisteredRef = useRef<boolean>(false)
  const vastUrlMacros = useRef<{ [key: string]: any } | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [mountPlayer, setMountPlayer] = useState<boolean>(false)
  const viewportType = useContext(ViewportTypeContext)
  const { showOverlay, hideOverlay, isOverlay } = useVideo()
  const { areAdSlotsLoaded, loadAllSlots } = useAds()

  const startMuted = isInVideoOnlyArticle || vtype === Vtype.LIVESTREAM

  const [videoPlayingStatus, setVideoPlayingStatus] =
    useState<VideoPlayingStatus>()

  const viewportOrientationContext = useContext(ViewportOrientationContext)

  const { videoRecommendations } = useVideoRecomendations({
    isBlickTV,
  })

  const { setCurrentTime, setAdPlaying, onPlaySuccess } = useBlickTVStory({
    playerRef: playerRef,
  })

  const reducedControls =
    isBlickTV || viewportType !== ViewportTypeValues.DESKTOP

  const onPiPButtonClick = useCallback(() => {
    popOut()
  }, [popOut])

  const onTimeout = useCallback(() => {
    console.error(
      'Timeout of 3000 milliseconds exceeded and ad server did not send back a response.'
    )
    captureException({
      reason:
        'Timeout of 3000 milliseconds exceeded and ad server did not send back a response.',
    })
    setMountPlayer(true)
  }, [])

  const adContainerId = `darkcove-video-container-${videoId}`
  const adSound = !startMuted

  const defaultDAIVastUrl = useMemo(
    () =>
      getDefaultDaiResponse({
        isMobile: viewportType === ViewportTypeValues.MOBILE,
        containerId: adContainerId,
        targeting: {
          sound: adSound,
        },
      }),
    [adContainerId, adSound, viewportType]
  )

  const handleCSAIAd = useCallback(
    ({ vmapUrl, message }: { vmapUrl?: string | null; message?: any }) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (message) {
        console.warn('CSAI AD MESSAGE', message)
      }
      if (vmapUrl) {
        vmapUrlRef.current = vmapUrl
      }
      setMountPlayer(true)
    },
    []
  )

  const handleDaiAd = useCallback(
    ({
      daiVastUrl,
      message,
    }: {
      daiVastUrl?: AdTagManagerDaiResponse
      message?: any
    }) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (message) {
        console.warn('DAI AD MESSAGE', message)
      }
      if (daiVastUrl?.queryString) {
        console.log('daiVastUrl', daiVastUrl?.queryString)
        vastUrlMacros.current = daiVastUrl?.queryString
      } else {
        console.log('default daiVastUrl', defaultDAIVastUrl.queryString)
        vastUrlMacros.current = defaultDAIVastUrl.queryString
      }
      setMountPlayer(true)
    },
    [defaultDAIVastUrl]
  )

  const getAutostartValue = useCallback(() => {
    /**
     * the targeting values have to be strings
     * in order to find out if the autoplay was blocked
     * by the browser in reload or first open, we use the
     * useIsServerSideRendered() hook
     */
    const autostart = `${!!isInVideoOnlyArticle}`
    return isSSR() ? 'false' : autostart
  }, [isInVideoOnlyArticle, isSSR])

  useEffect(() => {
    if (isAdDisabled !== false) {
      setMountPlayer(true)
    } else {
      if (tagManagerInitCode) {
        if (!hasAdRegisteredRef.current) {
          timeoutRef.current = setTimeout(onTimeout, 3000)

          hasAdRegisteredRef.current = true

          registerAd({
            isBlickTV,
            vtype,
            adContainerId,
            syndication,
            videoTaxonomy,
            sound: adSound,
            autostart: getAutostartValue(),
            handleAd: isBlickTV ? handleDaiAd : handleCSAIAd,
            areAdSlotsLoaded,
            loadAllSlots,
          })
        }
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [
    adContainerId,
    adSound,
    handleCSAIAd,
    handleDaiAd,
    isAdDisabled,
    isBlickTV,
    tagManagerInitCode,
    onTimeout,
    playerRef,
    syndication,
    videoTaxonomy,
    vtype,
    getAutostartValue,
    areAdSlotsLoaded,
    loadAllSlots,
  ])

  useEffect(() => {
    return () => {
      document.body.style.overflowY = 'auto'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      if (isBlickTV && allowFloating) {
        unmount()
      }
    }
  }, [allowFloating, isBlickTV, unmount])

  useEffect(() => {
    if (
      isOverlay &&
      viewportOrientationContext !== previousOrientationRef.current
    ) {
      trackingCallbacks?.onOrientationChange?.(viewportOrientationContext)
    }
    previousOrientationRef.current = viewportOrientationContext
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportOrientationContext, isOverlay])

  const enhancedTrackingCallbacks = {
    ...trackingCallbacks,

    onAdStarted: () => {
      setAdPlaying(true)
      trackingCallbacks?.onAdStarted?.()
    },
    onAdSkipped: () => {
      setAdPlaying(false)
      trackingCallbacks?.onAdSkipped?.()
    },
    onAdEnded: () => {
      setAdPlaying(false)
      trackingCallbacks?.onAdEnded?.()
    },

    onProgress: (obj?: { currentTime: number }) => {
      if (obj) {
        setCurrentTime(obj?.currentTime)
      }

      trackingCallbacks?.onProgress?.(obj)
    },

    onPlaySuccess: () => {
      onPlaySuccess?.()

      trackingCallbacks?.onPlaySuccess?.()
    },

    onEnterOverlay: () => {
      showOverlay()
      trackingCallbacks?.onEnterOverlay?.()
    },

    onExitOverlay: () => {
      hideOverlay()
      trackingCallbacks?.onExitOverlay?.()
    },

    onPause: ({ currentTime }: { currentTime: number }) => {
      if (
        // player fires onPause when the video has reached the end
        // to avoid it we check if currentTime is not the same as video duration
        // in this case - videoPlayingStatus will not be set to PAUSED
        currentTime &&
        currentTime !== playerRef.current?.player?.()?.duration?.()
      ) {
        setVideoPlayingStatus(VideoPlayingStatus.PAUSED)
      }
    },
    onPlay: () => {
      setVideoPlayingStatus(VideoPlayingStatus.PLAYING)
      trackingCallbacks?.onPlay?.()
    },

    onEnd: () => {
      setVideoPlayingStatus(VideoPlayingStatus.ENDED)
      trackingCallbacks?.onEnd?.()

      if (hasPlaylist && playlistVideosData) {
        const { items, isPlayingId } = playlistVideosData
        const nextPlaylistItem =
          items && isPlayingId
            ? getNextVideoInPlaylist(items, isPlayingId)
            : null

        if (nextPlaylistItem?.link?.href) {
          hideOverlay()
          router.push(nextPlaylistItem.link.href)
        }
      }
    },

    onCaptionsEnabled: () => {
      trackingCallbacks?.onCaptionsEnabled?.()
    },

    onCaptionsDisabled: () => {
      trackingCallbacks?.onCaptionsDisabled?.()
    },
  }

  return {
    ...restOfCommonProps,
    vtype,
    ref: playerRef,
    mountPlayer,
    playerId,
    ...enhancedTrackingCallbacks,
    isInViewport,
    autoplay: autoplay ? 'muted' : false,
    captionFontSize: isBlickTV && allowFloating ? 4.5 : 3.2,
    adContainerId,
    reducedControls,
    adTagURL: vmapUrlRef.current ?? '',
    vastUrl: vastUrlMacros.current ?? {},
    videoRecommendations,
    videoPlayingStatus,
    isOverlay,
    daiAssetKeyBrightcove,
    image,
    isMainElement,
    startMuted,
    size: !isOverlay && (isInArticle || hasPlaylist) ? 'small' : 'medium',
    allowFloating,
    onPiPButtonClick,
  }
}

export default usePlatformSpecificPropsWeb
